import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { BrandLogo } from "@/components/Brand";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const tokenMissing = !token;

  async function onSubmit(e) {
    e.preventDefault();
    if (pwd.length < 6) { setErr("La contraseña debe tener al menos 6 caracteres"); return; }
    if (pwd !== pwd2) { setErr("Las contraseñas no coinciden"); return; }
    setLoading(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Contraseña actualizada. Ya puedes iniciar sesión.");
      navigate("/login", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200">
        <CardContent className="p-8">
          <Link to="/" className="inline-block mb-6"><BrandLogo /></Link>
          <h1 className="font-display text-3xl font-light tracking-tight">Nueva contraseña</h1>
          <p className="text-slate-600 mt-2 text-sm">
            {tokenMissing
              ? "El enlace no es válido. Solicita uno nuevo desde el formulario de recuperación."
              : "Crea una nueva contraseña segura para tu cuenta."}
          </p>

          {tokenMissing ? (
            <Link to="/forgot-password" className="inline-block mt-6">
              <Button data-testid="reset-no-token-cta">Solicitar enlace nuevo</Button>
            </Link>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pwd">Nueva contraseña</Label>
                <div className="relative">
                  <Input
                    id="pwd" type={showPwd ? "text" : "password"} required minLength={6}
                    value={pwd} onChange={(e) => setPwd(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="reset-toggle-pwd">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwd2">Confirma la contraseña</Label>
                <Input
                  id="pwd2" type={showPwd ? "text" : "password"} required minLength={6}
                  value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                  placeholder="Repite la contraseña"
                  data-testid="reset-password-confirm"
                />
              </div>
              {err && <p className="text-sm text-red-700" data-testid="reset-error">{err}</p>}
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
                <Lock className="h-4 w-4 mr-2" /> {loading ? "Guardando…" : "Cambiar contraseña"}
              </Button>
            </form>
          )}

          <p className="text-sm text-slate-600 mt-6 text-center">
            <Link to="/login" className="text-[hsl(var(--accent))] underline" data-testid="reset-back-login">Volver al login</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
